import React, { useEffect, useRef, useState } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Spinner from "react-bootstrap/Spinner";
import ServerSideReactTable from "commons/table/ServerSideReactTable";
import { useDispatch, useSelector } from "react-redux";
import { updateCustomerList } from "./Customer.actions";
import { ColumnTypes } from "commons/table/Constants";
import { DEFAULT_PAGE_SIZE } from "./Customer.reducer";
import { FilterTypes } from "commons/table/Constants";
import { FaEye, FaPlus } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import ResetListState from "components/ResetListState";
import { formatDateForDisplay } from "services/MomentUtils";
import DismissableModal from "components/DismissableModal";
import CreateCustomerForm from "./CreateCustomerForm";
import { sendCustomersApiServiceAuthenticatedRequest } from "services/AsyncRequestService";
import { showNotification } from "services/NotificationService";
import { hasRoleIn } from "rbac/RbacHelper";
import PERMISSIONS from "rbac/Permissions.json";
import './CustomerList.css';

const CustomerList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const modalRef = useRef("CustomerList");
  const createFormRef = useRef(null);
  const skipDiscardConfirmRef = useRef(false);

  const { data, pages, loading, totalElements, listState } = useSelector(
    (state) => state.customerList
  );

  const canManage = hasRoleIn([PERMISSIONS.PERM_CONFIG_TOOL_MANAGE]);
  const [formKey, setFormKey] = useState(0);

  const renderLastModifiedDateCol = (date) => {
    if (date) {
      return formatDateForDisplay(date);
    }
    return <span className="center">N/A</span>;
  };

  useEffect(() => {
    dispatch(
      updateCustomerList(
        listState.pageSize,
        0,
        listState.sorted,
        listState.filtered
      )
    );
  }, [dispatch]);

  const onUpdateTable = (filters, sort, pageSize, page) => {
    const newListState = {
      page: page,
      pageSize: pageSize,
      sorted: sort ? sort : [],
      filtered: filters,
    };
    dispatch(
      updateCustomerList(
        newListState.pageSize,
        newListState.page,
        newListState.sorted,
        newListState.filtered
      )
    );
  };

  const handleCreateCustomer = (customerData, doneCallback) => {
    sendCustomersApiServiceAuthenticatedRequest(
      "/customers",
      "POST",
      customerData,
      () => {
        if (doneCallback) doneCallback();
        showNotification({
          title: "Success",
          message: "Customer created successfully!",
          type: "success",
          position: "tr",
        });
        handleCloseAndReset({ skipDiscard: true });
        onUpdateTable(listState.filtered, listState.sorted, listState.pageSize, listState.page);
      },
      (error) => {
        if (doneCallback) doneCallback();
        showNotification({
          title: "Error",
          message: "Error creating customer!",
          type: "error",
          position: "tr",
        });
        console.log(error);
      }
    );
  };

  const handleClose = () => {
    modalRef.current.handleClose();
  };

  const handleCloseAndReset = ({ skipDiscard = false } = {}) => {
    if (skipDiscard) {
      skipDiscardConfirmRef.current = true;
    }
    handleClose();
    setFormKey((prev) => prev + 1);
  };

  const handleRowClick = (row) => {
    const id = row?.original?.id;
    if (id) {
      navigate(`/admin/customer/${id}`);
    }
  };

  const columns = [
    {
      header: "Name",
      accessor: "name",
      accessorKey: "name",
      enableSorting: true,
      enableColumnFilter: true,
      ColumnType: ColumnTypes.Text,
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: "Customer Abbreviation",
      accessor: "customerNameAbbreviation",
      accessorKey: "customerNameAbbreviation",
      enableSorting: true,
      enableColumnFilter: true,
      ColumnType: ColumnTypes.Text,
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: "Created By",
      accessor: "createdByName",
      accessorKey: "createdByName",
      enableSorting: false,
      enableColumnFilter: false,
      ColumnType: ColumnTypes.Text,
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: "Date Created",
      accessor: "creationDate",
      accessorKey: "creationDate",
      show: true,
      enableSorting: true,
      enableColumnFilter: true,
      filterType: FilterTypes.DateRangePicker,
      placeDatePickerAtEnd: true,
      columnType: ColumnTypes.Custom,
      customComponent: (value) => {
        return renderLastModifiedDateCol(value);
      },
    },
    {
      header: "Actions",
      accessor: "action",
      accessorKey: "action",
      enableSorting: false,
      enableColumnFilter: false,
      columnType: ColumnTypes.Custom,
      customComponent: (value, row) => {
        return (
          <Link
            className="customer-list-action-link"
            to={`/admin/customer/${row.original.id}`}
            onClick={(e) => e.stopPropagation()}
          >
            <FaEye /> &nbsp; View
          </Link>
        );
      },
    },
  ];

  const totalDisplay = typeof totalElements === "number" ? totalElements : null;

  return (
    <Container className="list-container customer-list-page" fluid>
      <Row className="customer-list-header align-items-start mb-3">
        <Col md={8}>
          <h4 className="customer-list-header__title mb-2">Configuration Center</h4>
          <div className="customer-list-stat-pill">
            <span className="customer-list-stat-pill__label">Total Customers</span>
            <strong id="user-total-count" className="customer-list-stat-pill__value">
              {loading && totalDisplay === null ? (
                <Spinner animation="border" size="sm" />
              ) : (
                totalDisplay ?? "—"
              )}
            </strong>
          </div>
        </Col>
        <Col md={4}>
          <div className="d-flex align-items-center justify-content-end flex-wrap gap-2">
            {canManage && (
              <span className="mr-2">
                <DismissableModal
                  ref={modalRef}
                  openModalButtonText={
                    <>
                      <FaPlus className="mr-1" /> Add Customer
                    </>
                  }
                  openModalButtonStyle="primary"
                  openModalButtonBlock={false}
                  openModalButtonDisabled={false}
                  openModalButtonId="customer-create-btn-popup"
                  openModalButtonClassName="customer-list-add-btn"
                  preventCloseWhenClickedOutsideModal={true}
                  onClose={() => {
                    if (skipDiscardConfirmRef.current) {
                      skipDiscardConfirmRef.current = false;
                      setFormKey((prev) => prev + 1);
                      return;
                    }
                    if (createFormRef.current?.confirmDiscard && !createFormRef.current.confirmDiscard()) {
                      modalRef.current?.handleShow?.();
                      return;
                    }
                    setFormKey((prev) => prev + 1);
                  }}
                  hideFooter={true}
                  title="Create New Customer"
                  size="lg"
                >
                    <CreateCustomerForm
                      key={formKey}
                      ref={createFormRef}
                      onSubmit={(customerData, doneCallback) => {
                        handleCreateCustomer(customerData, doneCallback);
                      }}
                      onCancel={() => handleCloseAndReset({ skipDiscard: true })}
                    />
                </DismissableModal>
              </span>
            )}
            <ResetListState
              placedOnLightBg
              onReset={() => onUpdateTable([], [{ id: "name", desc: false }], DEFAULT_PAGE_SIZE, 0)}
            />
          </div>
        </Col>
      </Row>
      <Row>
        <Col md={12} className="customer-list-table-wrap">
          <ServerSideReactTable
            columnDefination={columns}
            data={data}
            loading={loading}
            defaultPageSize={DEFAULT_PAGE_SIZE}
            currentPage={listState.page}
            pageSize={listState.pageSize}
            totalPages={pages}
            totalRecords={
              totalElements === "Loading ..." ? 0 : totalElements
            }
            sorted={listState.sorted}
            filters={listState.filtered}
            enableMultiRowSelection={false}
            onUpdateTableData={onUpdateTable}
            onRowClick={handleRowClick}
            trStyle={() => ({ cursor: "pointer" })}
            dataTestId="customer-list"
          />
        </Col>
      </Row>
    </Container>
  );
};

export default CustomerList;
